// ============================================================================
// Exercises page — pure helpers (no React, no side effects).
// ----------------------------------------------------------------------------
// The catalog is fetched once and searched/faceted entirely client-side so
// search-as-you-type is INSTANT (no per-keystroke network round-trip). This
// module holds the locale-aware field resolution, the comma-joined muscle/
// equipment parsing (mirrors viccofitness/exercises._as_list), the ordered
// facet vocabularies (from the seed dataset + spec §6/§8), the lightweight
// search-rank scorer, and the badge-tone mappings. Everything is null-safe and
// "optional-first": a record can be just a name.
// ============================================================================
import type { Exercise } from "../../api";
import type { BadgeTone } from "../../components";

// --- locale field resolution ------------------------------------------------

export type ExLocale = "en" | "zh";

/** Resolve a localized field with English fallback, never returning blanks. */
function pick(
  ex: Exercise,
  enKey: keyof Exercise,
  zhKey: keyof Exercise,
  locale: ExLocale,
): string {
  const en = (ex[enKey] as string | null) ?? "";
  if (locale === "zh") {
    const zh = (ex[zhKey] as string | null) ?? "";
    return zh.trim() || en.trim();
  }
  return en.trim();
}

export function exerciseName(ex: Exercise, locale: ExLocale): string {
  return pick(ex, "name_en", "name_zh", locale) || "—";
}

export function exerciseDescription(ex: Exercise, locale: ExLocale): string {
  return pick(ex, "description_en", "description_zh", locale);
}

export function exerciseInstructions(ex: Exercise, locale: ExLocale): string {
  return pick(ex, "instructions_en", "instructions_zh", locale);
}

/**
 * Split free-text instructions into ordered steps. The seed stores them as a
 * single newline / sentence-joined blob; we split on hard breaks first, then
 * fall back to sentence boundaries so the detail view always reads as a list.
 */
export function instructionSteps(raw: string): string[] {
  const text = raw.trim();
  if (!text) return [];
  const byLine = text
    .split(/\r?\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (byLine.length > 1) return byLine;
  // Single paragraph → split on sentence ends, keeping it readable.
  return text
    .split(/(?<=[.!?。！？])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// --- comma-joined list parsing (mirrors backend _as_list) -------------------

/** A comma-joined muscle/field string → trimmed, de-duped list. */
export function toList(value: string | null | undefined): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of value.split(",")) {
    const v = part.trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      out.push(v);
    }
  }
  return out;
}

export function primaryMuscles(ex: Exercise): string[] {
  return toList(ex.primary_muscles);
}

export function secondaryMuscles(ex: Exercise): string[] {
  return toList(ex.secondary_muscles);
}

// --- modality (mirrors viccofitness.exercises.modality_for) -----------------

export type Modality = "resistance" | "circuit" | "cardio" | "mobility";

const CATEGORY_MODALITY: Record<string, Modality> = {
  strength: "resistance",
  powerlifting: "resistance",
  "olympic weightlifting": "resistance",
  strongman: "resistance",
  plyometrics: "circuit",
  cardio: "cardio",
  stretching: "mobility",
};

export function modalityOf(ex: Exercise): Modality {
  return CATEGORY_MODALITY[(ex.category ?? "").toLowerCase()] ?? "resistance";
}

// --- facet dimensions -------------------------------------------------------

export type FacetKey =
  | "muscle"
  | "movement_pattern"
  | "equipment"
  | "modality"
  | "level";

/** The active filter selection — one chosen value per facet (null = all). */
export type FilterState = Record<FacetKey, string | null>;

export function emptyFilters(): FilterState {
  return {
    muscle: null,
    movement_pattern: null,
    equipment: null,
    modality: null,
    level: null,
  };
}

export function activeFilterCount(f: FilterState): number {
  return (Object.keys(f) as FacetKey[]).reduce(
    (n, k) => n + (f[k] ? 1 : 0),
    0,
  );
}

// Canonical orderings from the seed dataset (scripts/seed_exercises +
// viccofitness/exercises). Unknown values discovered in the data are appended
// alphabetically so the chips stay correct even if the catalog grows.
const MUSCLE_ORDER = [
  "chest", "lats", "middle back", "lower back", "traps", "shoulders", "neck",
  "biceps", "triceps", "forearms", "abdominals", "glutes", "quadriceps",
  "hamstrings", "calves", "abductors", "adductors",
];
const PATTERN_ORDER = [
  "squat", "hinge", "lunge", "push", "pull", "carry", "rotation", "gait",
];
const EQUIPMENT_ORDER = [
  "body only", "dumbbell", "barbell", "cable", "machine", "kettlebells",
  "bands", "e-z curl bar", "exercise ball", "medicine ball", "foam roll",
  "other",
];
const MODALITY_ORDER: Modality[] = ["resistance", "cardio", "circuit", "mobility"];
const LEVEL_ORDER = ["beginner", "intermediate", "expert"];

const ORDERINGS: Record<FacetKey, readonly string[]> = {
  muscle: MUSCLE_ORDER,
  movement_pattern: PATTERN_ORDER,
  equipment: EQUIPMENT_ORDER,
  modality: MODALITY_ORDER,
  level: LEVEL_ORDER,
};

/** Sort a set of discovered facet values by the canonical order (rest A→Z). */
function orderValues(key: FacetKey, values: Set<string>): string[] {
  const order = ORDERINGS[key];
  const known: string[] = [];
  for (const v of order) if (values.has(v)) known.push(v);
  const extra = [...values].filter((v) => !order.includes(v)).sort();
  return [...known, ...extra];
}

export interface FacetOption {
  value: string;
  count: number;
}

export type FacetMap = Record<FacetKey, FacetOption[]>;

/**
 * Derive the available facet options + per-option counts from the catalog.
 * Counts reflect the WHOLE catalog (stable chip set); the result list itself
 * is filtered separately so chips don't flicker in/out as you refine.
 */
export function deriveFacets(exercises: Exercise[]): FacetMap {
  const buckets: Record<FacetKey, Map<string, number>> = {
    muscle: new Map(),
    movement_pattern: new Map(),
    equipment: new Map(),
    modality: new Map(),
    level: new Map(),
  };
  const bump = (key: FacetKey, value: string) => {
    const v = value.trim().toLowerCase();
    if (!v) return;
    buckets[key].set(v, (buckets[key].get(v) ?? 0) + 1);
  };

  for (const ex of exercises) {
    for (const m of primaryMuscles(ex)) bump("muscle", m);
    for (const m of secondaryMuscles(ex)) bump("muscle", m);
    if (ex.movement_pattern) bump("movement_pattern", ex.movement_pattern);
    if (ex.equipment) bump("equipment", ex.equipment);
    bump("modality", modalityOf(ex));
    if (ex.level) bump("level", ex.level);
  }

  const out = {} as FacetMap;
  for (const key of Object.keys(buckets) as FacetKey[]) {
    const map = buckets[key];
    out[key] = orderValues(key, new Set(map.keys())).map((value) => ({
      value,
      count: map.get(value) ?? 0,
    }));
  }
  return out;
}

// --- matching: facets + instant text search ---------------------------------

function matchesFacets(ex: Exercise, f: FilterState): boolean {
  if (f.movement_pattern && (ex.movement_pattern ?? "").toLowerCase() !== f.movement_pattern) {
    return false;
  }
  if (f.equipment && (ex.equipment ?? "").toLowerCase() !== f.equipment) {
    return false;
  }
  if (f.level && (ex.level ?? "").toLowerCase() !== f.level) {
    return false;
  }
  if (f.modality && modalityOf(ex) !== f.modality) {
    return false;
  }
  if (f.muscle) {
    const muscles = [...primaryMuscles(ex), ...secondaryMuscles(ex)].map((m) =>
      m.toLowerCase(),
    );
    if (!muscles.includes(f.muscle)) return false;
  }
  return true;
}

/**
 * Lightweight relevance score for a query against an exercise. Higher = better;
 * 0 = no match. Name hits outrank muscle / equipment hits, and a name PREFIX
 * outranks a mid-string hit, so typing "squ" floats "Squat" to the top.
 */
function scoreText(ex: Exercise, locale: ExLocale, q: string): number {
  const name = exerciseName(ex, locale).toLowerCase();
  // Search both locales' names so a zh user can still type English and vice-versa.
  const altName = (
    (ex.name_en ?? "") + " " + (ex.name_zh ?? "")
  ).toLowerCase();
  let score = 0;
  if (name.startsWith(q)) score = 100;
  else if (name.includes(q)) score = 70;
  else if (altName.includes(q)) score = 60;

  if (score === 0) {
    const muscles = [...primaryMuscles(ex), ...secondaryMuscles(ex)]
      .join(" ")
      .toLowerCase();
    if (muscles.includes(q)) score = 30;
    else if ((ex.equipment ?? "").toLowerCase().includes(q)) score = 20;
    else if ((ex.movement_pattern ?? "").toLowerCase().includes(q)) score = 15;
  }
  return score;
}

/**
 * Filter + (when searching) rank the catalog. Facets always apply; a non-empty
 * query then narrows + sorts by relevance. With no query the catalog stays in
 * its server order (name A→Z).
 */
export function filterExercises(
  exercises: Exercise[],
  filters: FilterState,
  query: string,
  locale: ExLocale,
): Exercise[] {
  const q = query.trim().toLowerCase();
  const faceted = exercises.filter((ex) => matchesFacets(ex, filters));
  if (!q) return faceted;

  const scored: Array<{ ex: Exercise; score: number }> = [];
  for (const ex of faceted) {
    const score = scoreText(ex, locale, q);
    if (score > 0) scored.push({ ex, score });
  }
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return exerciseName(a.ex, locale).localeCompare(exerciseName(b.ex, locale));
  });
  return scored.map((s) => s.ex);
}

// --- badge tones ------------------------------------------------------------

export function modalityTone(m: Modality): BadgeTone {
  switch (m) {
    case "cardio":
      return "info";
    case "circuit":
      return "warning";
    case "mobility":
      return "success";
    default:
      return "volt";
  }
}

export function levelTone(level: string | null | undefined): BadgeTone {
  switch ((level ?? "").toLowerCase()) {
    case "beginner":
      return "success";
    case "intermediate":
      return "warning";
    case "expert":
      return "ember";
    default:
      return "neutral";
  }
}

/** Title-case a lowercase facet value for display when there's no translation. */
export function titleCase(value: string): string {
  return value.replace(/\b\w/g, (c) => c.toUpperCase());
}
